import { autorun, computed, action, observable, makeObservable } from 'mobx'
import deepEqual from 'deep-equal'
import EditorModel from '@client/components/Editor/model'
import { HISTORY_DELAY, HISTORY_LIMIT } from '@client/components/constants'

export default class History {
  @observable index

  constructor(
    historyLimit = HISTORY_LIMIT,
    historyDelay = HISTORY_DELAY,
    item = new EditorModel()
  ) {
    makeObservable(this)
    this.states = null
    this.index = null
    this.isUndoRedo = false
    this.editorModel = item
    this.historyLimit = historyLimit
    this.historyDelay = historyDelay
    this.changeCallbacks = []

    this.clear()

    autorun(
      (reaction) => {
        const newItem = this.editorModel.serialize()

        if (this.editorModel.hasTextWithoutStyles || !this.editorModel.isValid) {
          return
        }

        if (!this.isUndoRedo && !deepEqual(newItem, this.states[this.index])) {
          const newIndex = this.index + 1
          const newStates = [...this.states.slice(0, newIndex), newItem]

          if (newStates.length > this.historyLimit) {
            newStates.shift()
          }
          const lastIndex = newStates.length - 1
          this.setIndex(newIndex > lastIndex ? lastIndex : newIndex)
          this.states = newStates
        } else {
          this.states[this.index] = newItem
          this.isUndoRedo = false
        }

        this.changeCallbacks.forEach((func) => {
          func(newItem)
        })
      },
      { delay: this.historyDelay }
    )
  }

  @computed get isUndoDisabled() {
    return this.index === 0 || this.index === null
  }

  @computed get isRedoDisabled() {
    return this.index === this.states.length - 1
  }

  onChange = (cb) => {
    this.changeCallbacks.push(cb)
  }

  offChange = (cb) => {
    this.changeCallbacks = this.changeCallbacks.filter((func) => func !== cb)
  }

  @action undo = () => {
    this.isUndoRedo = true
    const prevIndex = this.index - 1
    this.index = prevIndex < 0 ? 0 : prevIndex
    const current = this.getCurrentValue()
    this.editorModel.amend(current)
  }

  @action redo = () => {
    this.isUndoRedo = true
    const nextIndex = this.index + 1
    const lastIndex = this.states.length - 1
    this.index = nextIndex > lastIndex ? lastIndex : nextIndex
    this.editorModel.amend(this.getCurrentValue())
  }

  @action setIndex = (index) => {
    this.index = index
  }

  getCurrentValue = () => {
    return this.states[this.index]
  }

  clear = () => {
    this.states = [this.editorModel.serialize()]
    this.setIndex(0)
  }
}
